import { useEffect, useState, type RefObject } from 'react'

/**
 * Track which `data-entity-id`-tagged descendant currently has DOM focus
 * inside `ref`. Returns null when nothing inside is focused.
 *
 * Used by `useSectionPinShortcut` to know which row to act on when the user
 * presses `P` over the dashboard.
 */
export const useFocusedRowId = (ref: RefObject<HTMLElement | null>): string | null => {
  const [focusedId, setFocusedId] = useState<string | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }

    const readFocused = (target: EventTarget | null): string | null => {
      if (!(target instanceof HTMLElement)) {
        return null
      }
      const owner = target.closest<HTMLElement>('[data-entity-id]')
      return owner?.dataset.entityId ?? null
    }

    const onFocusIn = (ev: FocusEvent) => setFocusedId(readFocused(ev.target))
    const onFocusOut = () => setFocusedId(null)

    el.addEventListener('focusin', onFocusIn)
    el.addEventListener('focusout', onFocusOut)
    return () => {
      el.removeEventListener('focusin', onFocusIn)
      el.removeEventListener('focusout', onFocusOut)
    }
  }, [ref])

  return focusedId
}
